/* AIMAK · la capa de movimiento (la parte que no cabe en CSS).

   Cuatro cosas, y ninguna cambia lo que la vista dice:
     · indexar: pone `--i` en los hermanos que entran escalonados (motion.css).
     · números: las cifras grandes cuentan desde cero hasta su valor.
     · barras: las barras de avance crecen desde cero hasta su ancho.
     · transición: el cambio de vista pasa por View Transitions si el
       navegador las tiene; si no, el escalonado del CSS hace el trabajo.

   Todo respeta `prefers-reduced-motion`: con esa preferencia, las cifras se
   pintan de golpe y las barras aparecen ya llenas. */

const reducido = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Quién entra escalonado. Los mismos selectores que motion.css. */
const ESCALONADOS = [
  ".contenido > *", ".pcards > *", ".col > .jobcard", ".col > .pcard", ".kanban > .col", ".tut-lista > .tut",
  ".semana-horas > .dia-h", ".grid2 > *", ".grid3 > *", ".grid4 > *", ".fz-totales > *", ".tramos > *", ".dist-grid > *",
  ".splash .tarjetas > *", ".firma-lista > .firma-fila", ".candidatos > .cand", ".fila-act .horas > .celda-act",
];
function indexar(root: HTMLElement) {
  for (const sel of ESCALONADOS) {
    const porPadre = new Map<Element, number>();
    root.querySelectorAll<HTMLElement>(sel).forEach((el) => {
      const p = el.parentElement; if (!p) return;
      const i = porPadre.get(p) ?? 0;
      el.style.setProperty("--i", String(i));
      porPadre.set(p, i + 1);
    });
  }
}

/* Las cifras: solo el primer nodo de texto del elemento, para que un
   `0<small>/ 8</small>` cuente el 0 y deje el "/ 8" quieto. Un prefijo ($) y
   un sufijo (%, h) se conservan; los decimales y los miles, también. */
const CIFRAS = ".kpi .v b, .cifra, .fz-total .v, .semana-horas .dia-h .h, .cand .reco, .dist-partida .monto";
const RE_CIFRA = /^([^\d-]*)(-?\d{1,3}(?:,\d{3})+(?:\.\d+)?|-?\d+(?:\.\d+)?)(.*)$/s;
function numeros(root: HTMLElement) {
  root.querySelectorAll<HTMLElement>(CIFRAS).forEach((el) => {
    const nodo = el.firstChild;
    if (!nodo || nodo.nodeType !== Node.TEXT_NODE) return;
    const m = RE_CIFRA.exec((nodo.textContent ?? "").trim());
    if (!m) return;
    const pre = m[1] ?? "", num = m[2] ?? "", post = m[3] ?? "";
    const fin = Number(num.replace(/,/g, ""));
    if (!Number.isFinite(fin) || fin === 0) return;
    const dec = (num.split(".")[1] ?? "").length;
    const miles = num.includes(",");
    const fmt = (v: number) => {
      const s = v.toFixed(dec);
      if (!miles) return s;
      const [e, d] = s.split(".");
      return e!.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (d ? "." + d : "");
    };
    const dur = 650 + Math.min(450, Math.abs(fin) * 2);
    const t0 = performance.now();
    const paso = (t: number) => {
      const k = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      nodo.textContent = pre + fmt(fin * e) + post;
      if (k < 1) requestAnimationFrame(paso);
    };
    requestAnimationFrame(paso);
  });
}

/* Las barras llevan su medida en línea (`style="width:62%"`). Se guardan, se
   ponen a cero sin transición, y en el fotograma siguiente vuelven a su medida
   con la transición de motion.css. */
const ANCHO = ".barra > div, .fz-margen > .rel, .fz-fee > .rel";
const ALTO = ".barras-h .b, .dias-act .b, .minis .mini > div";
function barras(root: HTMLElement) {
  const prep = (sel: string, prop: "width" | "height") => {
    const lista: [HTMLElement, string][] = [];
    root.querySelectorAll<HTMLElement>(sel).forEach((el) => {
      const v = el.style[prop]; if (!v) return;
      lista.push([el, v]);
      el.style.transition = "none"; el.style[prop] = "0";
    });
    return lista;
  };
  const a = prep(ANCHO, "width"), b = prep(ALTO, "height");
  if (!a.length && !b.length) return;
  void root.offsetWidth;   // fuerza el reflow con las barras a cero
  requestAnimationFrame(() => {
    for (const [el, v] of a) { el.style.transition = ""; el.style.width = v; }
    for (const [el, v] of b) { el.style.transition = ""; el.style.height = v; }
  });
}

/** Lo que corre sobre cada vista recién pintada. */
export function animar(root: HTMLElement) {
  indexar(root);
  if (reducido()) return;
  numeros(root);
  barras(root);
}

/** El cambio de vista como una transición del documento, si el navegador sabe. */
type ConTransicion = Document & { startViewTransition?: (cb: () => void) => { finished: Promise<void> } };
export function transicion(pintar: () => void): Promise<void> {
  const d = document as ConTransicion;
  if (!d.startViewTransition || reducido() || document.visibilityState !== "visible") { pintar(); return Promise.resolve(); }
  try { return d.startViewTransition(pintar).finished.catch(() => undefined); }
  catch { pintar(); return Promise.resolve(); }
}

/* La onda al pulsar los botones que importan: nace donde cayó el dedo. */
const CON_ONDA = ".btn.primario, .btn.ok, .btn.oscuro, .fab, .compositor .enviar";
export function montarOndas() {
  document.addEventListener("pointerdown", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>(CON_ONDA);
    if (!b || reducido()) return;
    const r = b.getBoundingClientRect(); const d = Math.max(r.width, r.height);
    const s = document.createElement("span"); s.className = "onda";
    s.style.cssText = "width:" + d + "px;height:" + d + "px;left:" + (e.clientX - r.left - d / 2) + "px;top:" + (e.clientY - r.top - d / 2) + "px";
    b.appendChild(s);
    setTimeout(() => s.remove(), 700);
  }, { passive: true });
}

/* La barra del navegador del móvil toma el color del fondo del tema. */
export function temaColor() {
  let m = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!m) { m = document.createElement("meta"); m.name = "theme-color"; document.head.appendChild(m); }
  m.content = getComputedStyle(document.documentElement).getPropertyValue("--ground").trim() || "#F3F0E9";
}
